<template>
  <div>
    <!-- Treasure Box Popup -->
    <van-popup
      :value="value"
      @input="$emit('input', $event)"
      round
      :close-on-click-overlay="true"
      class="tb-popup-wrapper"
    >
      <div class="tb-popup">
        <button class="tb-popup__close" @click="handleClose">
          <van-icon name="cross" size="18" color="rgba(255,255,255,0.4)" />
        </button>

        <!-- Header -->
        <div class="tb-popup__header">
          <div class="tb-popup__grid-bg"></div>
          <p class="tb-popup__mission">{{ $lang.fd_mission_briefing || 'Mission Briefing' }}</p>

          <span class="tb-popup__value-label">{{ $lang.fd_total_reward_value || 'Total Reward Value' }}</span>
          <div class="tb-popup__value-row">
            <span class="tb-popup__value-currency">{{ liveCurrency }}</span>
            <span class="tb-popup__value-amount">{{ totalValue }}</span>
            <span class="tb-popup__value-plus">+</span>
            <span v-if="extraRewardText" class="tb-popup__value-extra">{{ extraRewardText }}</span>
          </div>
          <p class="tb-popup__subtitle">
            {{ $lang.tb_treasure_box || 'Treasure' }} <span class="tb-popup__highlight">{{ $lang.tb_box || 'Box' }}</span>
          </p>

          <!-- Countdown -->
          <div class="tb-popup__timer">
            <span class="tb-popup__timer-label">{{ $lang.fd_critical_deadline || 'Critical Deadline' }}</span>
            <span class="tb-popup__timer-digits">{{ tbCountdown }}</span>
          </div>
        </div>

        <!-- Action -->
        <div class="tb-popup__action">
          <div class="tb-popup__energy">
            <div class="tb-popup__energy-track">
              <div class="tb-popup__energy-fill" :style="{ width: tbEnergy + '%' }"></div>
            </div>
            <p class="tb-popup__energy-text">{{ $lang.fd_energy_critical || 'Energy Level: Critical Status' }}</p>
          </div>

          <button class="tb-popup__btn" :disabled="tbPaying || !isTreasurePurchaseAllowed()" @click="claimTreasureBox">
            <template v-if="tbPaying">{{ $lang.common_loading || 'Loading...' }}</template>
            <template v-else>
              <span class="tb-popup__btn-label">{{ $lang.fd_claim_now || 'Claim Now' }}</span>
              <span v-if="price" class="tb-popup__btn-price">
                {{ liveCurrency }} {{ $formatNumberWithCommas(price) }}
              </span>
            </template>
          </button>
          <p class="tb-popup__disclaimer">{{ $lang.fd_offer_ends_energy || 'Offer ends when energy runs out' }}</p>
        </div>
      </div>
    </van-popup>

    <!-- Payment iframe popup -->
    <van-popup
      v-model="tbPayShow"
      closeable
      close-icon-position="top-right"
      style="width: 90%; border-radius: 10px; height: 80%"
    >
      <div>
        <iframe
          :src="tbPayUrl"
          scrolling="no"
          style="width: 100%; height: 80vh"
          frameborder="0"
        ></iframe>
      </div>
    </van-popup>
    <login v-model="showLogin" overlay></login>
  </div>
</template>

<script>
import { VnRechargeInitS, Pay } from '@/api/common'
import { reportPromoPanel } from '@/utils/common'
import { failMessage as rewardFailMessage, hasUnavailableMarker, isNeedLogin, isOkResponse, responseContent } from '@/views/details/activity/rewardApi'
import { activityPaymentUnavailableMessage, selectActivityPaymentMethod } from '@/views/details/activity/activityPayment'
import { hasProvenEmptyActivityPaymentList } from '@/views/details/activity/activityPayment'
import {
  failMessage as paymentFlowFailMessage,
  getPaymentUrl as paymentFlowGetPaymentUrl,
  hasPaymentSuccessProof as paymentFlowHasPaymentSuccessProof,
  isRechargePayReady as isPaymentFlowRechargePayReady
} from '@/components/recharge/paymentFlow'
import { safeGetStorageItem, safeLocalStorage } from '@/utils/safeStorage'

function getLocalStorage() {
  try {
    if (typeof safeLocalStorage === 'function') return safeLocalStorage()
  } catch (e) { return null }
  return null
}

export default {
  name: 'TreasureBoxPopup',
  props: {
    // v-model for visibility
    value: {
      type: Boolean,
      default: false
    },
    // Treasure box price used for payment
    price: {
      type: [Number, String],
      default: 0
    },
    canPurchase: {
      type: [Boolean, Number, String],
      default: false
    },
    conditionCode: {
      type: [Number, String],
      default: ''
    },
    // Total reward value shown on header
    totalValue: {
      type: [Number, String],
      default: 0
    },
    // Initial countdown seconds
    countdownSeconds: {
      type: Number,
      default: 86399
    },
    // Energy bar percentage (0-100)
    energy: {
      type: Number,
      default: 65
    },
    // Extra rewards from backend (array of { amount, type })
    rewards: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      tbEnergy: this.energy,
      tbCountdownSeconds: this.countdownSeconds,
      tbCountdownTimer: null,
      tbPaying: false,
      tbPayShow: false,
      tbPayUrl: '',
      showLogin: false
    }
  },
  computed: {
    liveToken() {
      return safeGetStorageItem(getLocalStorage(), 'token')
    },
    // 实时从安全存储取，避免 App 初始化时还没登录拿到的快照过期
    liveCurrency() {
      return safeGetStorageItem(getLocalStorage(), 'currency') || this.getCurrency || ''
    },
    extraRewardText() {
      if (!Array.isArray(this.rewards) || this.rewards.length === 0) return ''
      return this.rewards
        .map(r => r && r.amount != null ? r.amount : '')
        .filter(Boolean)
        .join(' + ')
    },
    tbCountdown() {
      const h = Math.floor(this.tbCountdownSeconds / 3600)
      const m = Math.floor((this.tbCountdownSeconds % 3600) / 60)
      const s = this.tbCountdownSeconds % 60
      return (
        String(h).padStart(2, '0') + ':' +
        String(m).padStart(2, '0') + ':' +
        String(s).padStart(2, '0')
      )
    }
  },
  watch: {
    value(v, oldV) {
      if (v) {
        // reset countdown from latest prop each time popup opens
        this.tbCountdownSeconds = this.countdownSeconds
        this.tbEnergy = this.energy
        this.startTbCountdown()
        if (!oldV) reportPromoPanel('treasure_box', 1)
      } else {
        this.clearCountdown()
        if (oldV) reportPromoPanel('treasure_box', 2)
      }
    },
    countdownSeconds(v) {
      // Only reset when the popup is closed, otherwise we keep the running timer
      if (!this.value) {
        this.tbCountdownSeconds = v
      }
    },
    // 支付 iframe 弹窗关闭时（用户完成/取消付款），通知外层刷新宝箱状态
    tbPayShow(v, oldV) {
      if (oldV && !v) {
        this.$emit('payment-closed')
      }
    }
  },
  beforeDestroy() {
    this.clearCountdown()
  },
  methods: {
    requireLogin() {
      this.showLogin = true
      if (this.$bus) this.$bus.$emit('openLogin')
    },
    failMessage(res, fallback) {
      return rewardFailMessage(res, fallback || this.$lang.mc_claim_failed || 'Reward is temporarily unavailable. Please try again later.')
    },
    paymentFailMessage(res, fallback) {
      if (typeof paymentFlowFailMessage !== 'function') return this.failMessage(res, fallback)
      return paymentFlowFailMessage(res, fallback || 'Payment is temporarily unavailable. Please try again later.')
    },
    hasResponseContent(content) {
      return content &&
        typeof content === 'object' &&
        !Array.isArray(content) &&
        Object.keys(content).length > 0
    },
    isNegativeFlag(value) {
      if (value === false || value === 0) return true
      const normalized = String(value).trim().toLowerCase()
      return ['0', 'false', 'fail', 'failed', 'error', 'rejected', 'locked', 'not_receive', 'not_received', 'not_available'].includes(normalized)
    },
    paymentToken(value) {
      return String(value || '').trim().toLowerCase()
    },
    isPositiveFlag(value) {
      if (value === true || value === 1) return true
      const normalized = String(value).trim().toLowerCase()
      return ['1', 'true', 'yes', 'open', 'enabled', 'available'].includes(normalized)
    },
    isTreasurePurchaseAllowed() {
      return Number(this.price) > 0 && this.isPositiveFlag(this.canPurchase)
    },
    isUnavailablePaymentToken(value) {
      const token = this.paymentToken(value)
      return !!token && (
        ['stub', 'mock', 'mock_fallback', 'mock-fallback', 'mock fallback', 'fallback', 'fallback_used', 'fallback used', 'fallback-used', 'dataunavailable', 'data_unavailable', 'data unavailable', 'data-unavailable', 'read_unavailable', 'readunavailable', 'read unavailable', 'read-unavailable', 'route_unavailable', 'routeunavailable', 'route unavailable', 'route-unavailable', 'technical_error', 'technicalerror', 'technical error', 'technical-error', 'technical_marker', 'technicalmarker', 'technical marker', 'technical-marker', 'backend_failure', 'backendfailure', 'backend failure', 'backend-failure', 'technical_exception', 'technicalexception', 'technical exception', 'technical-exception', 'read_only', 'readonly', 'read only', 'read-only', 'unimplemented', 'not_implemented', 'not implemented', 'not_supported', 'not-supported', 'not supported', 'not_connected', 'not-connected', 'not connected', 'write_blocked', 'writeblocked', 'write blocked', 'write-blocked', 'backend_blocked', 'backendblocked', 'backend blocked', 'backend-blocked', 'blocked', 'unsupported', 'disabled', 'unavailable', 'need_login', 'placeholder', 'about:blank', '#', 'javascript:void(0)'].includes(token) ||
        token.includes('mock') ||
        token.includes('fallback') ||
        token.includes('dataunavailable') ||
        token.includes('data_unavailable') ||
        token.includes('data unavailable') ||
        token.includes('read_unavailable') ||
        token.includes('readunavailable') ||
        token.includes('read unavailable') ||
        token.includes('read-unavailable') ||
        token.includes('route_unavailable') ||
        token.includes('routeunavailable') ||
        token.includes('route unavailable') ||
        token.includes('route-unavailable') ||
        token.includes('technical_error') ||
        token.includes('technicalerror') ||
        token.includes('technical error') ||
        token.includes('technical-error') ||
        token.includes('technical_exception') ||
        token.includes('technical_marker') ||
        token.includes('technicalmarker') ||
        token.includes('technical marker') ||
        token.includes('backend_failure') ||
        token.includes('backendfailure') ||
        token.includes('backend failure') ||
        token.includes('technicalexception') ||
        token.includes('technical exception') ||
        token.includes('technical-exception') ||
        token.includes('技术错误') ||
        token.includes('技术异常') ||
        token.includes('read_only') ||
        token.includes('read only') ||
        token.includes('read-only') ||
        token.includes('unimplemented') ||
        token.includes('not_implemented') ||
        token.includes('not implemented') ||
        token.includes('not_supported') ||
        token.includes('not-supported') ||
        token.includes('not supported') ||
        token.includes('not_connected') ||
        token.includes('not-connected') ||
        token.includes('not connected') ||
        token.includes('write_blocked') ||
        token.includes('writeblocked') ||
        token.includes('write blocked') ||
        token.includes('write-blocked') ||
        token.includes('backend_blocked') ||
        token.includes('backendblocked') ||
        token.includes('backend blocked') ||
        token.includes('backend-blocked') ||
        token.includes('blocked') ||
        token.includes('need_login') ||
        token.includes('unsupported')
      )
    },
    hasBlockedReturnMarker(payload, seen = new Set()) {
      if (!payload || typeof payload !== 'object' || seen.has(payload)) return false
      seen.add(payload)
      return Object.entries(payload).some(([key, value]) => {
        const normalizedKey = String(key || '').trim().toLowerCase()
        if (['failclosed', 'fail_closed', 'falseProof', 'falseproof', 'false_proof', 'paymentStatus', 'paymentstatus', 'payment_status', 'paymentStatusUnconfirmed', 'paymentstatusunconfirmed', 'payment_status_unconfirmed', 'unconfirmed'].includes(normalizedKey) &&
          value !== false &&
          value !== 0 &&
          !['', '0', 'false', 'no', 'off'].includes(this.paymentToken(value))) return true
        if (['rewardproof', 'writeproof'].includes(normalizedKey) &&
          (value === false || value === 0 || ['0', 'false', 'no', 'off'].includes(this.paymentToken(value)))) return true
        if (['readonly', 'read_only', 'readonlyreward', 'read_only_reward', 'readunavailable', 'read_unavailable', 'routeunavailable', 'route_unavailable', 'technicalerror', 'technical_error', 'technicalexception', 'technical_exception', 'technicalmarker', 'technical_marker', 'backendfailure', 'backend_failure', 'mock', 'mock_fallback', 'mockfallback', 'stub', 'fallback', 'fallbackused', 'fallback_used', 'dataunavailable', 'data_unavailable', 'not_supported', 'notsupported', 'not_connected', 'notconnected', 'not_implemented', 'notimplemented', 'blocked', 'backendblocked', 'backend_blocked', 'rewardbackendblocked', 'reward_backend_blocked', 'writeblocked', 'write_blocked', 'failclosed', 'fail_closed', 'unimplemented'].includes(normalizedKey) &&
          (value === true || value === 1 || ['1', 'true', 'yes', 'on', 'mock', 'stub', 'fallback', 'fallback_used', 'data_unavailable', 'read_only', 'readonly', 'read_only_reward', 'readonlyreward', 'read_unavailable', 'readunavailable', 'route_unavailable', 'routeunavailable', 'technical_error', 'technicalerror', 'technical_exception', 'technicalexception', 'technical_marker', 'technicalmarker', 'backend_failure', 'backendfailure', 'not_supported', 'notsupported', 'not_connected', 'notconnected', 'not_implemented', 'notimplemented', 'unimplemented', 'blocked', 'backendblocked', 'backend_blocked', 'rewardbackendblocked', 'reward_backend_blocked', 'writeblocked', 'write_blocked', 'failclosed', 'fail_closed', 'fail closed'].includes(this.paymentToken(value)))) {
          return true
        }
        if (['message', 'msg', 'reason', 'status', 'state'].includes(normalizedKey)) {
          const text = this.paymentToken(value)
          if (text.includes('未接入') || text.includes('mock') || text.includes('stub') || text.includes('fallback') || text.includes('只读') || text.includes('阻止假成功') || text.includes('阻止假开箱') || text.includes('failclosed') || text.includes('fail closed') || text.includes('fail_closed') || text.includes('falseproof') || text.includes('false proof') || text.includes('false_proof') || text.includes('paymentstatusunconfirmed') || text.includes('payment status unconfirmed') || text.includes('payment_status_unconfirmed') || text.includes('unconfirmed') || text.includes('writeblocked') || text.includes('write blocked') || text.includes('write_blocked') || text.includes('backendblocked') || text.includes('backend blocked') || text.includes('backend_blocked') || text.includes('backendfailure') || text.includes('backend failure') || text.includes('backend_failure') || text.includes('rewardbackendblocked') || text.includes('reward backend blocked') || text.includes('reward_backend_blocked') || text.includes('not_connected') || text.includes('not connected') || text.includes('not_supported') || text.includes('not supported') || text.includes('not_implemented') || text.includes('not implemented') || text.includes('routeunavailable') || text.includes('route unavailable') || text.includes('route_unavailable') || text.includes('readunavailable') || text.includes('read unavailable') || text.includes('read_unavailable') || text.includes('technicalerror') || text.includes('technical error') || text.includes('technical_error') || text.includes('technicalmarker') || text.includes('technical marker') || text.includes('technical_marker') || text.includes('technicalexception') || text.includes('technical exception') || text.includes('technical_exception') || text.includes('技术错误') || text.includes('技术异常') || text.includes('unimplemented')) return true
        }
        return value && typeof value === 'object' && this.hasBlockedReturnMarker(value, seen)
      })
    },
    isUsablePaymentMethod(payment) {
      const source = payment && typeof payment === 'object' ? payment : {}
      const key = this.paymentToken(source.paymentKey || source.paymentId)
      const status = this.paymentToken(source.status || source.paymentStatus || source.channelStatus)
      const closeValues = [source.isClose, source.closed, source.close].map(this.paymentToken)
      const enabledValues = [source.enabled, source.enable, source.isEnabled, source.open, source.isOpen]
      if (!key || hasUnavailableMarker(source) || this.isUnavailablePaymentToken(key) || this.isUnavailablePaymentToken(status)) return false
      if (['disabled', 'closed', 'off', 'offline', 'unavailable'].includes(status)) return false
      if (closeValues.some(value => ['1', 'true', 'yes'].includes(value))) return false
      return !enabledValues.some(value => value === false || ['0', 'false', 'no'].includes(this.paymentToken(value)))
    },
    hasExplicitFailure(content) {
      if (!content || typeof content !== 'object') return false
      return [
        content.paySuccess,
        content.success,
        content.status,
        content.state
      ].some(value => this.isNegativeFlag(value))
    },
    paymentUrl(content) {
      if (typeof paymentFlowGetPaymentUrl === 'function') return this.safePaymentUrl(paymentFlowGetPaymentUrl(content))
      if (!content || typeof content !== 'object') return ''
      return this.safePaymentUrl(content.url ||
        content.payUrl ||
        content.paymentUrl ||
        content.cashierUrl ||
        content.checkoutUrl ||
        content.redirectUrl ||
        content.qrcode ||
        content.qrCode ||
        '')
    },
    isSafeBusinessProof(value) {
      const text = String(value || '').trim()
      if (!text) return false
      const token = this.paymentToken(text)
      return !this.isUnavailablePaymentToken(token) && ![
        '0',
        'none',
        'null',
        'undefined',
        'placeholder',
        'mock',
        'about:blank',
        '#',
        'javascript:void(0)'
      ].includes(token)
    },
    safePaymentUrl(value) {
      const text = String(value || '').trim()
      if (!this.isSafeBusinessProof(text) || text.charAt(0) === '#') return ''
      if (/^[a-z][a-z0-9+.-]*:/i.test(text) && !/^https?:\/\//i.test(text)) return ''
      return text
    },
    hasPaymentSuccessProof(res) {
      return paymentFlowHasPaymentSuccessProof(res)
    },
    hasPaymentFailure(content) {
      if (!content || typeof content !== 'object') return false
      if (hasUnavailableMarker(content) || this.hasBlockedReturnMarker(content)) return true
      const status = String(content.status || content.state || '').trim().toLowerCase()
      return [
        content.paySuccess,
        content.success
      ].some(value => this.isNegativeFlag(value)) ||
        ['fail', 'failed', 'error', 'rejected'].includes(status)
    },
    isPaymentReady(res) {
      const payContent = responseContent(res)
      if (hasUnavailableMarker(res) || this.hasBlockedReturnMarker(res)) return false
      const payUrl = this.paymentUrl(payContent)
      if (!payUrl) return false
      if (!this.hasPaymentSuccessProof(res)) return false
      if (typeof isPaymentFlowRechargePayReady === 'function') {
        return isPaymentFlowRechargePayReady(res) && !!payUrl
      }
      if (!isOkResponse(res) || !this.hasResponseContent(payContent)) return false
      return !this.hasPaymentFailure(payContent)
    },
    isPaymentInitReady(res, content, selectedPayment) {
      return isOkResponse(res) &&
        !hasUnavailableMarker(res) &&
        !this.hasBlockedReturnMarker(res) &&
        this.hasResponseContent(content) &&
        this.isUsablePaymentMethod(selectedPayment)
    },
    handleClose() {
      this.$emit('input', false)
    },
    startTbCountdown() {
      if (this.tbCountdownTimer) clearInterval(this.tbCountdownTimer)
      this.tbCountdownTimer = setInterval(() => {
        if (this.tbCountdownSeconds > 0) {
          this.tbCountdownSeconds--
        } else {
          this.clearCountdown()
        }
      }, 1000)
    },
    clearCountdown() {
      if (this.tbCountdownTimer) {
        clearInterval(this.tbCountdownTimer)
        this.tbCountdownTimer = null
      }
    },
    async claimTreasureBox() {
      if (this.tbPaying) return
      if (!this.liveToken) {
        this.requireLogin()
        return
      }
      if (!this.isTreasurePurchaseAllowed()) {
        this.$toast({ message: this.$lang.tb_not_available || this.$lang.feature_unavailable || 'This feature is temporarily unavailable. Please try again later.', icon: 'cross' })
        return
      }
      this.tbPayShow = false
      this.tbPayUrl = ''
      this.tbPaying = true
      try {
        // 获取第一种支付方式
        const initRes = await VnRechargeInitS()
        if (isNeedLogin(initRes)) {
          this.requireLogin()
          return
        }
        const initContent = responseContent(initRes)
        const selectedPayment = selectActivityPaymentMethod(initContent.paymentList || initContent)
        const hasProvenEmptyPayments = typeof hasProvenEmptyActivityPaymentList === 'function' &&
          (hasProvenEmptyActivityPaymentList(initRes) || hasProvenEmptyActivityPaymentList(initContent))
        if (hasProvenEmptyPayments && !selectedPayment && this.hasResponseContent(initContent)) {
          this.$toast({ message: activityPaymentUnavailableMessage(), icon: 'cross' })
          return
        }
        if (!this.isPaymentInitReady(initRes, initContent, selectedPayment)) {
          this.$toast({ message: this.failMessage(initRes, 'Payment is temporarily unavailable. Please try again later.'), icon: 'cross' })
          return
        }
        // 直接以 price 金额发起付款
        const payRes = await Pay({
          paymentAmount: this.price,
          paymentId: selectedPayment.paymentId,
          paymentKey: selectedPayment.paymentKey,
          paymentType: selectedPayment.paymentType,
          rechargeFees: selectedPayment.rechargeFees,
          phone: initContent._account,
          promoType: 'treasure_box',
          conditionCode: this.conditionCode
        })
        const payContent = responseContent(payRes)
        if (isNeedLogin(payRes)) {
          this.requireLogin()
        } else if (this.isPaymentReady(payRes)) {
          this.$emit('input', false)
          this.tbPayUrl = this.paymentUrl(payContent)
          this.tbPayShow = true
        } else {
          this.$toast({ message: this.paymentFailMessage(payRes, 'Payment is temporarily unavailable. Please try again later.'), icon: 'cross' })
        }
      } catch (error) {
        this.$toast({ message: this.failMessage(error, this.$lang.request_failed || 'Request failed. Please try again later.'), icon: 'cross' })
      } finally {
        this.tbPaying = false
      }
    }
  }
}
</script>

<style lang="less" scoped>
/* ====== Treasure Box Popup (Compact) ====== */
@tb-primary: @brand;   /* 绿→火焰橙(主色/CTA/激活) */
@tb-yellow: @gold;     /* 黄→金(宝箱奖励金额) */
@tb-red: #f5455c;      /* 红保留语义(错误/亏),统一 --vk-danger */
@tb-bg: @vk-bg;        /* 深页面底 */

.tb-popup-wrapper {
  background: transparent !important;
  overflow: visible !important;
}

.tb-popup {
  position: relative;
  width: 300px;
  background: @tb-bg;
  border: 2px solid rgba(30, 41, 59, 1);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.6);

  &__close {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 10;
    background: none;
    border: none;
    cursor: pointer;
  }

  /* ---- Header Section ---- */
  &__header {
    position: relative;
    padding: 28px 20px 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    background: rgba(15, 23, 42, 0.4);
    overflow: hidden;
  }

  &__grid-bg {
    position: absolute;
    inset: 0;
    background-image: radial-gradient(fade(@tb-primary, 10%) 1px, transparent 1px);
    background-size: 20px 20px;
    opacity: 0.3;
    pointer-events: none;
  }

  &__mission {
    position: relative;
    z-index: 1;
    font-size: var(--vk-fs-xs);
    font-weight: 900;
    color: rgba(255, 255, 255, 0.4);
    letter-spacing: 3px;
    text-transform: uppercase;
    margin-bottom: 16px;
  }

  &__value-label {
    position: relative;
    z-index: 1;
    font-size: 9px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.5);
    text-transform: uppercase;
    letter-spacing: 3px;
    margin-bottom: 4px;
  }

  &__value-row {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    flex-wrap: nowrap;
    white-space: nowrap;
  }

  &__value-currency {
    font-size: var(--vk-fs-lg);
    font-weight: 900;
    color: @tb-yellow;
    margin-top: 10px;
  }

  &__value-amount {
    font-size: 56px;
    line-height: 1;
    font-weight: 900;
    color: @tb-yellow;
    letter-spacing: -2px;
    filter: drop-shadow(0 0 16px fade(@tb-yellow, 35%));
  }

  &__value-plus {
    font-size: var(--vk-fs-lg);
    font-weight: 900;
    color: @tb-yellow;
    margin-top: 10px;
    margin-left: 2px;
  }

  &__value-extra {
    font-size: var(--vk-fs-2xl);
    line-height: 1;
    font-weight: 900;
    color: @tb-yellow;
    margin-left: 2px;
    margin-top: 8px;
    filter: drop-shadow(0 0 10px fade(@tb-yellow, 35%));
  }

  &__subtitle {
    position: relative;
    z-index: 1;
    font-size: var(--vk-fs-xs);
    font-weight: 700;
    color: rgba(255, 255, 255, 0.6);
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-top: 4px;
    margin-bottom: 12px;
  }

  &__highlight {
    color: @tb-yellow;
  }

  /* ---- Countdown ---- */
  &__timer {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 220px;
    background: rgba(0, 0, 0, 0.4);
    padding: 10px 16px;
    border-radius: 12px;
    border: 1px solid fade(@tb-primary, 20%); /* 降噪:倒计时改品牌色,去警报红 */

    &-label {
      font-size: 9px;
      font-weight: 900;
      color: fade(@tb-primary, 80%);
      text-transform: uppercase;
      letter-spacing: 2px;
      margin-bottom: 4px;
    }

    &-digits {
      font-size: var(--vk-fs-2xl);
      font-weight: 900;
      color: @tb-primary;
      letter-spacing: 1px;
      text-shadow: 0 0 12px fade(@tb-primary, 60%);
    }
  }

  /* ---- Action Section ---- */
  &__action {
    padding: 16px 20px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__energy {
    width: 100%;
    margin-bottom: 12px;

    &-track {
      width: 100%;
      height: 10px;
      background: #0f172a;
      border-radius: 999px;
      padding: 2px;
      border: 1px solid rgba(255, 255, 255, 0.05);
      box-shadow: inset 0 -2px 4px rgba(0, 0, 0, 0.5);
    }

    &-fill {
      height: 100%;
      background: linear-gradient(to right, #c2410c, @tb-primary);
      border-radius: 999px;
      box-shadow: 0 0 8px @tb-primary;
    }

    &-text {
      font-size: 8px;
      color: var(--vk-t3);
      text-transform: uppercase;
      font-weight: 900;
      letter-spacing: 1px;
      margin-top: 4px;
      text-align: center;
    }
  }

  &__btn {
    width: 100%;
    background: @tb-primary;
    color: var(--vk-bg);
    font-weight: 900;
    padding: 10px 12px;
    border-radius: 10px;
    border: none;
    text-transform: uppercase;
    box-shadow: 0 4px 0 #b3360f; /* 火焰橙暗调阴影(原绿 #1ba863) */
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    line-height: 1.2;

    &:active {
      box-shadow: none;
      transform: translateY(4px);
    }

    &:disabled {
      opacity: 0.6;
    }
  }

  &__btn-label {
    font-size: var(--vk-fs-md);
    letter-spacing: 0.5px;
  }

  &__btn-price {
    font-size: var(--vk-fs-xs);
    font-weight: 700;
    opacity: 0.7;
    text-transform: none;
    letter-spacing: 0;
  }

  &__disclaimer {
    margin-top: 8px;
    font-size: 8px;
    color: #475569;
    text-transform: uppercase;
    font-weight: 700;
    letter-spacing: 1px;
  }
}
</style>
